import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const getApiBase = () =>
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) ||
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_URL) ||
  ""; // empty = same-origin (www.gastreaming.live)

function normalizeRecordJson(data) {
  // Support BOTH server formats:
  // A) { wins, losses }
  // B) { w, l, record }
  const wins = Number(data?.wins ?? data?.w ?? 0);
  const losses = Number(data?.losses ?? data?.l ?? 0);
  return { wins, losses };
}

const WIN_MILESTONE_VARIANTS = [
  {
    key: "fireworks",
    kicker: "5 WIN MILESTONE",
    title: "GET MFN ACTIVE",
    sub: "THE STREAK IS TALKING NOW",
  },
  {
    key: "heat",
    kicker: "WIN STREAK HEAT CHECK",
    title: "PETE IN HIS BAG",
    sub: "THE METER JUST OVERLOADED",
  },
  {
    key: "boss",
    kicker: "BOSS MODE ACTIVATED",
    title: "RUN IT UP",
    sub: "ANOTHER 5 PIECE ON THE BOARD",
  },
  {
    key: "crowd",
    kicker: "CROWD POP",
    title: "THE STREAK CONTINUES",
    sub: "GET ACTIVE ENERGY ONLY",
  },
];

const LOSS_MILESTONE_VARIANTS = [
  {
    key: "loss",
    kicker: "3 LOSS CHECK-IN",
    title: "LOCK BACK IN",
    sub: "DJ GETACTIVE SAID REGROUP",
  },
];

function pickMilestoneVariant(list, wins, losses) {
  const seed = Math.abs((wins * 31 + losses * 17) % list.length);
  return list[seed] || list[0];
}

export default function OverlayRecord() {
  const { channelId } = useParams();
  const [sp] = useSearchParams();

  const roomCandidates = useMemo(() => {
    const qRoom = (sp.get("room") || "").trim().toLowerCase();
    if (qRoom) return [qRoom];

    const base = ((channelId || "").trim().toLowerCase() || "").replace(/^vod:/, "");
    if (!base) return [];

    // IMPORTANT: Your API is returning room: "stream:getactive_pet3"
    // so we try that first, then fallbacks.
    return [`stream:${base}`, base, `vod:${base}`];
  }, [channelId, sp]);

  const apiBase = getApiBase();
  const [record, setRecord] = useState({ wins: 0, losses: 0 });
  const [dbg, setDbg] = useState("");

  // FX (flash/pulse)
  const prevRef = useRef({ wins: 0, losses: 0, init: false });
  const [fx, setFx] = useState(""); // "win" | "loss" | "reset" | ""
  const fxTimerRef = useRef(null);

  // Milestone FX: every 5th win, every 3rd loss.
  const [milestone, setMilestone] = useState(null);
  const milestoneTimerRef = useRef(null);

  // Force transparency for OBS
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    const prev = {
      htmlBg: html.style.background,
      htmlBgImg: html.style.backgroundImage,
      bodyBg: body.style.background,
      bodyBgImg: body.style.backgroundImage,
      bodyMargin: body.style.margin,
      bodyOverflow: body.style.overflow,
    };

    html.style.background = "transparent";
    html.style.backgroundImage = "none";
    body.style.background = "transparent";
    body.style.backgroundImage = "none";
    body.style.margin = "0";
    body.style.overflow = "hidden";

    return () => {
      html.style.background = prev.htmlBg;
      html.style.backgroundImage = prev.htmlBgImg;
      body.style.background = prev.bodyBg;
      body.style.backgroundImage = prev.bodyBgImg;
      body.style.margin = prev.bodyMargin;
      body.style.overflow = prev.bodyOverflow;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    async function fetchRoom(room) {
      const res = await fetch(`${apiBase}/api/wl?room=${encodeURIComponent(room)}`, {
        credentials: "include",
      });
      const data = await res.json();
      return { roomReturned: data?.room || room, ...normalizeRecordJson(data) };
    }

    async function tick() {
      if (!roomCandidates.length) return;

      try {
        let best = null;

        for (const room of roomCandidates) {
          const r = await fetchRoom(room);

          if (!best) best = r;

          // Prefer any non-zero record immediately
          if (r.wins !== 0 || r.losses !== 0) {
            best = r;
            break;
          }
        }

        if (!alive || !best) return;

        setRecord({ wins: best.wins, losses: best.losses });
        setDbg(`ok: req(${roomCandidates.join(", ")}) -> got(${best.roomReturned})`);
      } catch (e) {
        if (!alive) return;
        setDbg("offline");
      }
    }

    tick();
    const id = setInterval(tick, 1200);

    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [apiBase, roomCandidates]);

  // Trigger FX when record changes
  useEffect(() => {
    const prev = prevRef.current;

    // Skip FX on very first load to avoid flashing when OBS loads.
    if (!prev.init) {
      prevRef.current = { wins: record.wins, losses: record.losses, init: true };
      return;
    }

    const winUp = record.wins > prev.wins;
    const lossUp = record.losses > prev.losses;
    const isReset = record.wins === 0 && record.losses === 0 && (prev.wins !== 0 || prev.losses !== 0);

    let nextFx = "";
    if (isReset) nextFx = "reset";
    else if (winUp) nextFx = "win";
    else if (lossUp) nextFx = "loss";

    if (nextFx) {
      setFx(nextFx);
      if (fxTimerRef.current) clearTimeout(fxTimerRef.current);
      fxTimerRef.current = setTimeout(() => setFx(""), 420);
    }

    let nextMilestone = null;
    if (winUp && record.wins > 0 && record.wins % 5 === 0) {
      nextMilestone = {
        type: "win",
        record: `${record.wins}-${record.losses}`,
        ...pickMilestoneVariant(WIN_MILESTONE_VARIANTS, record.wins, record.losses),
      };
    } else if (lossUp && record.losses > 0 && record.losses % 3 === 0) {
      nextMilestone = {
        type: "loss",
        record: `${record.wins}-${record.losses}`,
        ...pickMilestoneVariant(LOSS_MILESTONE_VARIANTS, record.wins, record.losses),
      };
    }

    if (nextMilestone) {
      setMilestone(nextMilestone);
      if (milestoneTimerRef.current) clearTimeout(milestoneTimerRef.current);
      milestoneTimerRef.current = setTimeout(() => setMilestone(null), nextMilestone.type === "win" ? 6200 : 4300);
    }

    prevRef.current = { wins: record.wins, losses: record.losses, init: true };
  }, [record.wins, record.losses]);

  useEffect(() => {
    return () => {
      if (fxTimerRef.current) clearTimeout(fxTimerRef.current);
      if (milestoneTimerRef.current) clearTimeout(milestoneTimerRef.current);
    };
  }, []);

  // Position controls
  // Keep the OBS/browser source full-screen so milestone FX can use the whole canvas,
  // but position the small record counter independently inside that canvas.
  const fontSize = Number(sp.get("size") || 64);
  const right = Number(sp.get("right") || 24);
  const left = Number(sp.get("left") || 24);
  const top = Number(sp.get("top") || 24);
  const bottom = Number(sp.get("bottom") || 24);
  const pos = String(sp.get("pos") || "top-right").trim().toLowerCase();

  const recordPosition = (() => {
    if (pos === "top-left") return { top, left };
    if (pos === "bottom-left") return { bottom, left };
    if (pos === "bottom-right") return { bottom, right };
    return { top, right }; // default: top-right
  })();

  const glow =
    fx === "win"
      ? "rgba(0,255,120,0.85)"
      : fx === "loss"
      ? "rgba(255,70,70,0.85)"
      : fx === "reset"
      ? "rgba(255,255,255,0.75)"
      : "rgba(0,0,0,0.85)";

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        background: "transparent",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Keyframes for record pop + milestone overlays */}
      <style>{`
        @keyframes gaPulse {
          0%   { transform: scale(1);   filter: brightness(1); }
          40%  { transform: scale(1.12); filter: brightness(1.25); }
          100% { transform: scale(1);   filter: brightness(1); }
        }

        @keyframes gaMilestoneIn {
          0% { opacity: 0; transform: translateY(22px) scale(0.88) rotate(-1.5deg); filter: blur(5px); }
          12% { opacity: 1; transform: translateY(0) scale(1.04) rotate(0.7deg); filter: blur(0); }
          22% { transform: translateY(0) scale(1) rotate(0deg); }
          82% { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-18px) scale(0.94); }
        }

        @keyframes gaFireworkPop {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.15); }
          12% { opacity: 1; }
          76% { opacity: 0.9; }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(1.65); }
        }

        @keyframes gaSpinGlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes gaSweep {
          0% { transform: translateX(-120%) skewX(-20deg); opacity: 0; }
          25% { opacity: 0.5; }
          60% { opacity: 0.18; }
          100% { transform: translateX(120%) skewX(-20deg); opacity: 0; }
        }

        @keyframes gaLossFlicker {
          0%, 100% { opacity: 0.72; transform: translateY(0); }
          24% { opacity: 1; transform: translateY(-2px); }
          48% { opacity: 0.62; transform: translateY(2px); }
          72% { opacity: 0.95; transform: translateY(0); }
        }
      `}</style>

      <div
        style={{
          position: "fixed",
          ...recordPosition,
          fontFamily: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
          fontSize,
          lineHeight: 1,
          color: "#fff",
          textShadow: `0 2px 12px ${glow}`,
          userSelect: "none",
          pointerEvents: "none",
          animation: fx ? "gaPulse 0.38s ease-out" : "none",
        }}
      >
        {record.wins}-{record.losses}
      </div>

      {milestone && (
        <MilestoneOverlay milestone={milestone} />
      )}

      {sp.get("debug") === "1" && (
        <div
          style={{
            position: "fixed",
            left: 10,
            top: 10,
            padding: "6px 10px",
            borderRadius: 8,
            background: "rgba(0,0,0,0.6)",
            color: "#fff",
            fontSize: 12,
            pointerEvents: "none",
            maxWidth: 520,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {dbg || "…"}
        </div>
      )}
    </div>
  );
}

function MilestoneOverlay({ milestone }) {
  const isWin = milestone.type === "win";
  const accent = isWin ? "#ffcc33" : "#ff4040";
  const secondary = isWin ? "#ff1111" : "#9a0000";
  const titleShadow = isWin
    ? "0 0 18px rgba(255, 20, 20, 0.9), 0 0 42px rgba(255, 204, 51, 0.7)"
    : "0 0 18px rgba(255, 40, 40, 0.8), 0 0 34px rgba(0,0,0,0.9)";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
        background: isWin
          ? "radial-gradient(circle at center, rgba(255,0,0,0.16), rgba(0,0,0,0) 62%)"
          : "radial-gradient(circle at center, rgba(90,0,0,0.25), rgba(0,0,0,0) 64%)",
        animation: "gaMilestoneIn 6.2s ease-in-out forwards",
      }}
    >
      {isWin && (
        <>
          <FireworkBurst left="18%" top="24%" delay="0s" />
          <FireworkBurst left="82%" top="22%" delay="0.18s" />
          <FireworkBurst left="24%" top="72%" delay="0.34s" />
          <FireworkBurst left="76%" top="74%" delay="0.5s" />
          <FireworkBurst left="50%" top="18%" delay="0.68s" />
        </>
      )}

      <div
        style={{
          position: "absolute",
          inset: "13% 8%",
          borderRadius: 34,
          border: `2px solid ${isWin ? "rgba(255,204,51,0.32)" : "rgba(255,50,50,0.26)"}`,
          background: isWin
            ? "linear-gradient(135deg, rgba(25,0,0,0.18), rgba(255,0,0,0.09), rgba(255,204,51,0.08))"
            : "linear-gradient(135deg, rgba(0,0,0,0.18), rgba(120,0,0,0.18), rgba(0,0,0,0.06))",
          boxShadow: isWin
            ? "0 0 55px rgba(255,0,0,0.22), inset 0 0 55px rgba(255,204,51,0.12)"
            : "0 0 44px rgba(255,0,0,0.18), inset 0 0 42px rgba(0,0,0,0.16)",
          opacity: 0.95,
        }}
      />

      <div
        style={{
          position: "absolute",
          width: "130vw",
          height: "22vh",
          top: "39vh",
          left: "-15vw",
          background: `linear-gradient(90deg, transparent, ${isWin ? "rgba(255,255,255,0.28)" : "rgba(255,70,70,0.18)"}, transparent)`,
          animation: "gaSweep 2.1s ease-in-out 0.25s 2",
          filter: "blur(1px)",
        }}
      />

      <div
        style={{
          width: "min(1080px, 86vw)",
          minHeight: isWin ? 330 : 260,
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          position: "relative",
          transform: milestone.key === "boss" ? "skewX(-3deg)" : "none",
          animation: milestone.type === "loss" ? "gaLossFlicker 0.9s ease-in-out infinite" : "none",
        }}
      >
        {milestone.key === "heat" && <RotatingRing color={accent} />}
        {milestone.key === "boss" && <BossBars accent={accent} secondary={secondary} />}
        {milestone.key === "crowd" && <ConfettiField />}

        <div
          style={{
            fontFamily: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
            letterSpacing: "0.16em",
            fontSize: "clamp(18px, 2.3vw, 34px)",
            color: isWin ? "#fff3b0" : "#ffb5b5",
            textShadow: "0 3px 12px rgba(0,0,0,0.85)",
            marginBottom: 6,
          }}
        >
          {milestone.kicker}
        </div>

        <div
          style={{
            fontFamily: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
            textTransform: "uppercase",
            fontSize: isWin ? "clamp(62px, 10vw, 148px)" : "clamp(52px, 8vw, 118px)",
            lineHeight: 0.9,
            color: isWin ? "#ffffff" : "#f7f7f7",
            WebkitTextStroke: isWin ? "2px rgba(255,0,0,0.85)" : "2px rgba(120,0,0,0.9)",
            textShadow: titleShadow,
          }}
        >
          {milestone.title}
        </div>

        <div
          style={{
            marginTop: 18,
            display: "inline-flex",
            gap: 18,
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            fontFamily: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
            letterSpacing: "0.12em",
            fontSize: "clamp(20px, 2.6vw, 38px)",
            color: accent,
            textShadow: "0 2px 12px rgba(0,0,0,0.9)",
          }}
        >
          <span>{milestone.sub}</span>
          <span style={{ color: "#fff" }}>RECORD {milestone.record}</span>
        </div>
      </div>
    </div>
  );
}

function FireworkBurst({ left, top, delay }) {
  return (
    <div
      style={{
        position: "absolute",
        left,
        top,
        width: 18,
        height: 18,
        transform: "translate(-50%, -50%)",
        borderRadius: "999px",
        background: "#fff",
        boxShadow:
          "0 -72px 0 #ffcc33, 0 72px 0 #ff3333, 72px 0 0 #fff, -72px 0 0 #ffcc33, 52px 52px 0 #ff3333, -52px 52px 0 #fff, 52px -52px 0 #ffcc33, -52px -52px 0 #ff3333",
        filter: "drop-shadow(0 0 12px rgba(255,204,51,0.95))",
        animation: `gaFireworkPop 1.75s ease-out ${delay} 3 both`,
      }}
    />
  );
}

function RotatingRing({ color }) {
  return (
    <div
      style={{
        position: "absolute",
        width: "min(76vw, 760px)",
        height: "min(76vw, 760px)",
        borderRadius: "999px",
        border: `10px dashed ${color}`,
        opacity: 0.16,
        animation: "gaSpinGlow 7s linear infinite",
        filter: "blur(0.3px)",
      }}
    />
  );
}

function BossBars({ accent, secondary }) {
  return (
    <>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "9%",
          right: "9%",
          height: 22,
          borderRadius: 999,
          background: `linear-gradient(90deg, ${secondary}, ${accent}, #ffffff)`,
          boxShadow: `0 0 20px ${secondary}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: "16%",
          right: "16%",
          height: 14,
          borderRadius: 999,
          background: `linear-gradient(90deg, #ffffff, ${accent}, ${secondary})`,
          opacity: 0.75,
        }}
      />
    </>
  );
}

function ConfettiField() {
  const pieces = Array.from({ length: 18 }, (_, i) => i);
  return pieces.map((i) => (
    <span
      key={i}
      style={{
        position: "absolute",
        left: `${8 + ((i * 13) % 84)}%`,
        top: `${4 + ((i * 19) % 70)}%`,
        width: 10 + (i % 4) * 5,
        height: 5 + (i % 3) * 4,
        borderRadius: 4,
        background: i % 3 === 0 ? "#ffcc33" : i % 3 === 1 ? "#ff3333" : "#ffffff",
        boxShadow: "0 0 10px rgba(255,255,255,0.45)",
        transform: `rotate(${i * 23}deg)`,
        opacity: 0.72,
      }}
    />
  ));
}
